// SmoothScroll - animated scrolling to in-page anchors for [data-scroll]
// links. Behavioral port of v1 modules/SmoothScroll.js; the HTML contract
// ([data-scroll], data-scroll-offset) is unchanged.
//
// v1 delegated to the smooth-scroll vendor (archived upstream); v2 uses the
// native `window.scrollTo({ behavior: 'smooth' })` instead. The vendor's
// easing/speed knobs have no native equivalent and are dropped; users with
// prefers-reduced-motion get an instant jump.

import { resolveScrollOffset, type ScrollOffset } from '../utils';

export interface SmoothScrollConfig {
  offset: ScrollOffset;
  /** Push the anchor hash into the address bar (v1 default: off). */
  updateURL: boolean;
}

const defaultConfig: SmoothScrollConfig = {
  offset: 0,
  updateURL: false,
};

export default class SmoothScroll {
  selector: string;

  config: SmoothScrollConfig;

  constructor(selector = '[data-scroll]', config: Partial<SmoothScrollConfig> = {}) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };

    this._init();
  }

  update = (): void => {
    this.destroy();
    this._init();
  };

  destroy = (): void => {
    document.removeEventListener('click', this._handleClick);
  };

  /** Scroll to an element, offset by the trigger's data-scroll-offset. */
  scrollTo = (target: HTMLElement, trigger: Element | null = null): void => {
    const offset = resolveScrollOffset(trigger, this.config.offset, target);
    const top = Math.max(
      target.getBoundingClientRect().top + window.pageYOffset - offset,
      0,
    );

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    // Move focus along with the viewport - BEFORE scrolling: focusing an
    // element cancels an in-progress smooth scroll in Chromium, even with
    // preventScroll (the vendor worked around this by focusing after the
    // animation).
    if (!target.hasAttribute('tabindex') && target.tabIndex < 0) {
      target.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });

    window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  private _init = (): void => {
    document.addEventListener('click', this._handleClick);
  };

  private _handleClick = (e: Event): void => {
    const trigger = (e.target as Element).closest?.(this.selector);

    if (!(trigger instanceof HTMLAnchorElement) || !trigger.hash) {
      return;
    }

    // Only anchors within the current page.
    if (
      trigger.hostname !== window.location.hostname ||
      trigger.pathname !== window.location.pathname
    ) {
      return;
    }

    const target = document.getElementById(decodeURIComponent(trigger.hash.slice(1)));

    if (!target) {
      return;
    }

    e.preventDefault();

    if (this.config.updateURL) {
      window.history.pushState(null, '', trigger.hash);
    }

    this.scrollTo(target, trigger);
  };
}
